/*
  Aus Serversätzen werden Stammdaten.

  Der Server hat Sitzung, Tagesordnung und Vorlagen über die
  OParl-Schnittstelle aus dem Ratsinformationssystem geholt und in eigenen
  Sammlungen abgelegt. Diese Datei setzt daraus zusammen, was die Oberfläche
  erwartet, und fügt hinzu, was der Fraktion gehört.

  ---------------------------------------------------------------
  Die Brücke zu den Volltexten
  ---------------------------------------------------------------

  Jeder Satz vom Server trägt in `oparl_id` die vollständige Adresse beim
  Ratsinformationssystem, etwa

      https://…/oparl/papers/vl-184

  Der letzte Teil davon — `vl-184` — ist zugleich die Kennung, unter der die
  Volltexte im Projekt liegen und auf die alle gespeicherten Markierungen,
  Meinungsbilder und Aufgaben zeigen. Daran hängt die ganze Zuordnung.

  Das ist eine Vereinfachung des Prototyps und wird in der echten Software so
  nicht funktionieren: Ein fremdes Ratsinformationssystem vergibt Adressen wie
  `…/papers/29481`, und Volltexte liegen dann nicht mehr im Projekt, sondern
  kommen als PDF mit. Siehe VEREINFACHUNGEN.md, Abschnitt 1.
*/

import {
  eigeneStammdaten,
  ergaenzungen,
  fraktionssitzung,
  sitzungsnummer,
  weitereKanaele,
} from "./demodaten";
import { mitUhrzeit, plusTage } from "../hilfen/datum";

/*
  Zeitpunkt vom Server in ein Datum verwandeln.

  PocketBase schreibt „2026-08-06 16:00:00.000Z“ — mit Leerzeichen statt des
  „T“, das die Norm vorsieht. Chrome nimmt das hin, Safari nicht immer, und
  Safari ist auf dem iPad der Regelfall. Ohne diese Zeile stünde dort „Invalid
  Date“ statt der Sitzung.
*/
function alsDatum(wert) {
  if (!wert) return null;
  const datum = new Date(String(wert).replace(" ", "T"));
  return isNaN(datum.getTime()) ? null : datum;
}

/* Der letzte Teil einer Adresse: aus „…/papers/vl-184“ wird „vl-184“. */
function kennung(oparlId) {
  const teile = String(oparlId || "").split("/").filter(Boolean);
  return teile.length ? teile[teile.length - 1] : "";
}

/*
  Zu welcher Vorlage gehört welcher Volltext?

  Die Volltexte im Projekt nennen ihre Vorlage selbst (`vorlageId`). Damit
  braucht es keine zweite Verabredung — auch dann nicht, wenn die Verwaltung
  eine Vorlage ohne Unterlagen liefert.
*/
function dateiZuVorlage(dateien) {
  const zuordnung = {};
  Object.values(dateien).forEach((datei) => {
    if (datei.vorlageId) zuordnung[datei.vorlageId] = datei.id;
  });
  return zuordnung;
}

function vorlageAus(satz, dateiId) {
  const id = kennung(satz.oparl_id);
  const stamm = satz.aktenzeichen_stamm || satz.aktenzeichen || "";
  const zusatz = ergaenzungen.vorlagen[stamm] || {};

  /*
    Der Stand vom Server beschreibt, ob die Verwaltung die Vorlage noch führt.
    Das ist etwas anderes als der Beratungsstand, den die Oberfläche zeigt —
    deshalb wird nur überschrieben, wenn etwas Auffälliges passiert ist.
  */
  let status = zusatz.beratungsstand || "in Beratung";
  if (satz.stand === "zurueckgezogen") status = "von der Verwaltung zurückgezogen";
  if (satz.stand === "archiviert") status = "archiviert";

  return {
    id,
    aktenzeichen: satz.aktenzeichen || "",
    aktenzeichenStamm: stamm,
    fassung: satz.fassung || 1,
    kurztitel: zusatz.kurztitel || satz.titel || "",
    titel: satz.titel || "",
    typ: satz.typ || "",
    status,
    stand: satz.stand || "aktuell",
    archiviert: satz.stand === "archiviert",
    federfuehrung: satz.federfuehrung || "",
    dateiId,
    /* Wird gleich nachgetragen, sobald alle Kennungen bekannt sind. */
    vorgaengerId: null,
    nachfolgerId: null,
    ohneUnterlagen: Boolean(satz.ohne_unterlagen),
    /* Ein Antrag der eigenen Fraktion ist an seiner Art zu erkennen. */
    eigenerAntrag: (satz.typ || "").indexOf("Antrag der Fraktion") === 0,
  };
}

/*
  Der Terminplan — alle Sitzungen, nicht nur die nächste.

  `stammdaten.sitzungen` enthält bewusst weiterhin genau zwei Einträge: die
  nächste Ausschusssitzung und die Fraktionssitzung davor. Daran hängen der
  Sitzungsraum, die Kanalzuordnung und das Meinungsbild; dort etwas zu
  ändern hieße, sechs Ansichten anzufassen, damit der Kalender etwas
  anzeigen kann.

  Der Kalender bekommt deshalb eine eigene, flache Liste daneben.

  Vor jeder Sitzung steht eine Fraktionssitzung — Dienstag davor, 18:30 Uhr,
  dieselbe Regel wie bisher für die eine. Die vor der nächsten Sitzung trägt
  die Kennung `s-fraktion`, damit Kalender und Sitzungsansicht von derselben
  reden und eine Zusage an beiden Stellen zählt.
*/
function baueTerminplan(sitzungen, naechsteId) {
  const eintraege = [];

  sitzungen.forEach((satz) => {
    const beginn = alsDatum(satz.beginn);
    if (!beginn) return;

    const id = kennung(satz.oparl_id);

    eintraege.push({
      id,
      art: "sitzung",
      name: satz.gremium || satz.name || "Sitzung",
      nummer: sitzungsnummer(beginn),
      beginn,
      ort: satz.ort || "",
      abgesagt: satz.stand === "abgesagt",
      istNaechste: id === naechsteId,
    });

    eintraege.push({
      id: id === naechsteId ? "s-fraktion" : `s-fraktion-${id}`,
      art: "fraktionssitzung",
      name: "Fraktionssitzung",
      beginn: mitUhrzeit(plusTage(beginn, -2), 18, 30),
      ort: "Fraktionszimmer 2.14, Rathaus",
      bereitetVor: id,
      istNaechste: id === naechsteId,
    });
  });

  return eintraege.sort((a, b) => a.beginn - b.beginn);
}

function sitzungAus(satz, gremiumId) {
  const beginn = alsDatum(satz.beginn);

  return {
    id: kennung(satz.oparl_id),
    gremiumId,
    beginn,
    /* Laufende Nummer in der Wahlperiode — aus dem Termin gerechnet. */
    nummer: sitzungsnummer(beginn),
    ort: satz.ort || "",
    art: satz.name || "Sitzung",
    abgesagt: satz.stand === "abgesagt",
    hinweis:
      "Ladung und Tagesordnung stammen aus dem Ratsinformationssystem der Verwaltung.",
  };
}

function topAus(satz, sitzungId, vorlageIdVonSatz) {
  const id = kennung(satz.oparl_id);
  const zusatz = ergaenzungen.tagesordnung[id] || {};
  const vorlageId = vorlageIdVonSatz || null;

  return {
    id,
    sitzungId,
    nummer: satz.nummer || "",
    titel: satz.titel || "",
    oeffentlich: satz.oeffentlich !== false,
    vorlageId,
    /*
      Punkte ohne Vorlage sind die formalen: Eröffnung, Niederschrift,
      Mitteilungen, Anfragen. Sie lassen sich nicht öffnen, weil es dahinter
      nichts zu lesen gibt.
    */
    formal: !vorlageId,
    nichtoeffentlichGrund: zusatz.nichtoeffentlichGrund || null,
    weitereVorlagenIds: zusatz.weitereVorlagen || [],
  };
}

/*
  Alles zusammensetzen.

  Gibt entweder { stammdaten } oder { fehler } zurück — die Oberfläche muss
  wissen, ob sie etwas anzeigen kann. Ohne Sitzung gibt es keinen Anker, und
  ohne Anker ergibt die ganze App keinen Sinn.
*/
/*
  Die Mitglieder der Fraktion — aus den echten Konten auf dem Server.

  Früher standen hier dreizehn erfundene Personen. Das war für eine Vorführung
  bequem und im Betrieb schädlich: Sobald echte Mitglieder dazukamen, ließ
  sich nicht mehr auseinanderhalten, wer wirklich etwas gesagt hat.

  Wer angemeldet ist, bestimmt der Server. Die App erfindet niemanden mehr.
*/
function personenAus(mitglieder) {
  const personen = {};
  (mitglieder || []).forEach((satz) => {
    personen[satz.id] = {
      id: satz.id,
      name: satz.name || satz.email || "Ohne Namen",
      rolle: satz.rolle === "mitglied" ? "Mitglied" : satz.rolle || "Mitglied",
      /*
        Wer in welchem Ausschuss sitzt, weiß heute niemand — das käme über
        OParl oder müsste die Fraktion pflegen. Bis dahin gehören alle zur
        Fraktion, und das ist die einzige Zugehörigkeit, die ausgewertet wird.
      */
      gremien: ["g-fraktion"],
      bild: satz.avatar || null,
    };
  });
  return personen;
}

export function baueStammdaten({ sitzungen, tagesordnung, vorlagen, mitglieder, ichId }) {
  const naechste = naechsteSitzung(sitzungen);
  if (!naechste) {
    /*
      Drei Ursachen führen zur selben leeren Liste, und alle drei sind
      wahrscheinlich genug, um sie zu nennen. Die dritte trifft der Server als
      Zugriffsregel: Wer keiner Fraktion zugeordnet ist, sieht nichts — von
      außen nicht von „es gibt nichts“ zu unterscheiden.
    */
    return {
      fehler:
        "Der Server nennt keine Sitzung. Möglich ist dreierlei: Der Abgleich " +
        "mit dem Ratsinformationssystem ist noch nicht gelaufen, die " +
        "Verwaltung hat noch keine Tagesordnung veröffentlicht, oder dein " +
        "Konto gehört noch zu keiner Fraktion.",
    };
  }

  /*
    Das Gremium kommt als Name mit der Sitzung. Die Oberfläche erwartet an
    dieser Stelle einen Eintrag im Gremienverzeichnis, deshalb wird einer
    daraus gebildet.
  */
  const gremiumId = `g-sitzung-${naechste.id}`;
  const ausschuss = sitzungAus(naechste, gremiumId);
  const gremien = {
    ...eigeneStammdaten.gremien,
    [gremiumId]: {
      id: gremiumId,
      name: naechste.gremium || "Gremium",
      art: "Ausschuss",
    },
  };

  const dateiVon = dateiZuVorlage(eigeneStammdaten.dateien);

  /* Kennung des Serversatzes → unsere Kennung, für den Verweis der Punkte. */
  const vorlageKennung = {};
  const vorlagenNachId = {};
  vorlagen.forEach((satz) => {
    const eigene = kennung(satz.oparl_id);
    vorlageKennung[satz.id] = eigene;
    vorlagenNachId[eigene] = vorlageAus(satz, dateiVon[eigene] || null);
  });

  /*
    Die Fassungskette in beide Richtungen begehbar machen.

    Der Server merkt sich nur, wovon eine Fassung abstammt. Die Anwendung
    braucht beide Richtungen: Wer die alte Fassung liest, muss erfahren, dass
    es eine neue gibt — sonst arbeitet er an einem Text weiter, der nicht mehr
    gilt. Und wer die neue liest, muss wissen, wo seine bisherigen
    Markierungen geblieben sind.
  */
  vorlagen.forEach((satz) => {
    if (!satz.vorgaenger) return;
    const neue = vorlagenNachId[vorlageKennung[satz.id]];
    const alte = vorlagenNachId[vorlageKennung[satz.vorgaenger]];
    if (!neue || !alte) return;
    neue.vorgaengerId = alte.id;
    alte.nachfolgerId = neue.id;
  });

  const punkte = tagesordnung
    .filter((satz) => satz.sitzung === naechste.id)
    .map((satz) => topAus(satz, ausschuss.id, vorlageKennung[satz.vorlage] || null));

  /*
    Ob eine Vorlage nichtöffentlich ist, sagt OParl nicht — wohl aber, ob der
    Tagesordnungspunkt es ist, auf dem sie beraten wird. Das ist dieselbe
    Aussage, nur an anderer Stelle, und sie kommt von der Verwaltung selbst.
  */
  punkte
    .filter((top) => !top.oeffentlich)
    .forEach((top) => {
      [top.vorlageId, ...top.weitereVorlagenIds].forEach((id) => {
        if (id && vorlagenNachId[id]) vorlagenNachId[id].nichtoeffentlich = true;
      });
    });

  const eigene = fraktionssitzung(ausschuss.beginn, ausschuss.id);

  /*
    Die Kanäle der Fraktion.

    Zwei stehen fest: der Kanal der ganzen Fraktion und der des Gremiums, das
    als nächstes tagt. Letzterer heißt immer `k-ausschuss`, damit die
    Beispielbeiträge ihn finden — seinen *Namen* bekommt er von der
    Verwaltung, denn welcher Ausschuss das ist, weiß nur sie.

    Die übrigen sind eine benannte Ergänzung im Projekt. In der echten
    Software kämen sie ebenfalls über OParl: Die Verwaltung veröffentlicht,
    welche Gremien es gibt.
  */
  const kanaele = [
    {
      id: "k-fraktion",
      name: eigeneStammdaten.gremien["g-fraktion"].name,
      art: "Fraktion",
    },
    {
      id: "k-ausschuss",
      name: naechste.gremium || "Ausschuss",
      art: "Ausschuss",
      sitzungId: ausschuss.id,
    },
    ...weitereKanaele,
  ];

  const personen = personenAus(mitglieder);

  /*
    Ohne eigenes Konto in der Personenliste ginge nichts: Jede Ansicht fragt
    „bin das ich?“. Das kann nur schiefgehen, wenn der Server das eigene Konto
    nicht mitliefert — dann steht hier lieber ein ehrlicher Platzhalter als
    ein Absturz.
  */
  if (ichId && !personen[ichId]) {
    personen[ichId] = { id: ichId, name: "Du", rolle: "Mitglied", gremien: ["g-fraktion"] };
  }

  return {
    stammdaten: {
      ...eigeneStammdaten,
      personen,
      ichId,
      /* Fragen für die Fraktionssitzung entstehen erst durch Benutzung. */
      fragen: [],
      gremien,
      vorlagen: vorlagenNachId,
      sitzungen: { [ausschuss.id]: ausschuss, "s-fraktion": eigene },
      /* Alle Sitzungen für den Kalender — siehe baueTerminplan. */
      terminplan: baueTerminplan(sitzungen, ausschuss.id),
      tagesordnung: punkte,
      ausschussId: ausschuss.id,
      kanaele,
      /* Zu welchem Kanal gehört ein Tagesordnungspunkt? */
      kanalDerSitzung: { [ausschuss.id]: "k-ausschuss", "s-fraktion": "k-fraktion" },
    },
  };
}

/*
  Welche Sitzung ist die nächste?

  Die kommende, sonst die zuletzt gewesene. Abgesagte zählen nicht — außer es
  gibt gar keine andere; dann ist die Absage die Nachricht.
*/
function naechsteSitzung(sitzungen) {
  const jetzt = Date.now();
  const brauchbar = sitzungen.filter((s) => alsDatum(s.beginn));
  if (!brauchbar.length) return null;

  const nachZeit = (a, b) => alsDatum(a.beginn) - alsDatum(b.beginn);
  const laufend = brauchbar.filter((s) => s.stand !== "abgesagt");
  const auswahl = laufend.length ? laufend : brauchbar;

  const kommende = auswahl
    .filter((s) => alsDatum(s.beginn).getTime() >= jetzt)
    .sort(nachZeit);
  if (kommende.length) return kommende[0];

  return auswahl.sort(nachZeit)[auswahl.length - 1];
}
